/**
 * Product Grid Swiper Functionality
 * Handles SwiperJS initialization for product grid sections
 */

// Prevent duplicate class declaration if script is loaded multiple times
if (typeof window.SuniceProductGridSwiper === 'undefined') {
  var SuniceProductGridSwiper = class {
  constructor() {
    this.swiperInstances = new Map();
  }

  /** @param {HTMLElement | null | undefined} el */
  _detachProductGridResizeListener(el) {
    if (!el) return;
    if (el._suniceProductGridResizeHandler) {
      window.removeEventListener('resize', el._suniceProductGridResizeHandler);
      delete el._suniceProductGridResizeHandler;
      delete el.dataset.resizeHandlerAttached;
    }

    const wrapper = el.closest?.('.swiper-container-wrapper');
    const prevButton = wrapper?.querySelector('.product-grid-nav-prev');
    if (prevButton?._suniceHomepageResetHandler) {
      prevButton.removeEventListener('click', prevButton._suniceHomepageResetHandler, true);
      delete prevButton._suniceHomepageResetHandler;
    }
  }

  initializeProductGridSwiper(swiperContainer) {
    if (!swiperContainer) {
      return;
    }
    
    // Check if already initialized - if so, destroy first (for theme editor updates)
    const containerId = swiperContainer.id || `product-grid-swiper-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    if (!swiperContainer.id) {
      swiperContainer.id = containerId;
    }
    
    if (this.swiperInstances.has(containerId)) {
      const existingSwiper = this.swiperInstances.get(containerId);
      this._detachProductGridResizeListener(swiperContainer);
      if (existingSwiper && existingSwiper.destroy) {
        existingSwiper.destroy(true, true);
      }
      this.swiperInstances.delete(containerId);
    }
    
    // Reset initialization flags
    delete swiperContainer.dataset.initStarted;
    swiperContainer.classList.remove('swiper-initialized');
    
    // Lazy initialization: only initialize if visible or about to be visible
    if (!swiperContainer.dataset.initStarted) {
      swiperContainer.dataset.initStarted = 'true';
      
      // In theme editor, always initialize immediately
      if (window.Shopify && window.Shopify.designMode) {
        this._doInitialize(swiperContainer);
        return;
      }
      
      // Check if already visible
      const rect = swiperContainer.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight + 200 && rect.bottom > -200;
      
      if (isVisible) {
        // Initialize immediately if visible
        this._doInitialize(swiperContainer);
      } else {
        // Use IntersectionObserver for lazy loading
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this._doInitialize(entry.target);
              observer.unobserve(entry.target);
            }
          });
        }, { rootMargin: '200px' });
        
        observer.observe(swiperContainer);
      }
      return;
    }
    
    // If already started, proceed with initialization
    this._doInitialize(swiperContainer);
  }
  
  _doInitialize(swiperContainer) {
    if (!swiperContainer) {
      return;
    }
    
    // Ensure container has a unique ID - this is critical for multiple sections
    if (!swiperContainer.id) {
      swiperContainer.id = `product-grid-swiper-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    
    const containerId = swiperContainer.id;
    
    // Check if this swiper is already initialized
    if (this.swiperInstances.has(containerId)) {
      // Destroy existing swiper and reinitialize to handle DOM updates
      const existingSwiper = this.swiperInstances.get(containerId);
      this._detachProductGridResizeListener(swiperContainer);
      if (existingSwiper && existingSwiper.destroy) {
        existingSwiper.destroy(true, true);
      }
      this.swiperInstances.delete(containerId);
    }
    
    // Function to initialize the swiper (called after slides are confirmed)
    const initializeSwiper = () => {
      // Count slides to determine if loop is possible
      const slideCount = swiperContainer.querySelectorAll('.swiper-slide').length;
      if (slideCount === 0) {
        return;
      }
      
      // Get settings from the swiper container's data attributes
      const showArrows = swiperContainer.dataset.showArrows !== 'false';
      const autoplay = swiperContainer.dataset.autoplay === 'true';
      const autoplaySpeed = swiperContainer.dataset.autoplaySpeed || 5000;
      const infinite = swiperContainer.dataset.infinite !== 'false';
      const isHomepageGrid = Boolean(swiperContainer.closest('.sunice-product-grid-section--homepage'));
      
      // Get CSS variables for spacing and column count
      const computedStyle = getComputedStyle(swiperContainer);
      let columnCount = parseInt(computedStyle.getPropertyValue('--product-grid-column-count')) || 4;
      let mobileColumnCount = parseInt(computedStyle.getPropertyValue('--product-grid-mobile-column-count')) || 1;
      
      // Fallback: try reading from inline style if CSS variable isn't set
      if (!columnCount || columnCount === 0 || isNaN(columnCount)) {
        const inlineStyle = swiperContainer.getAttribute('style') || '';
        const columnMatch = inlineStyle.match(/--product-grid-column-count:\s*(\d+)/);
        if (columnMatch) {
          columnCount = parseInt(columnMatch[1]);
        }
        // Final fallback
        if (!columnCount || columnCount === 0 || isNaN(columnCount)) {
          columnCount = 4;
        }
      }
      
      if (!mobileColumnCount || mobileColumnCount === 0 || isNaN(mobileColumnCount)) {
        mobileColumnCount = 1;
      }
      
      // Find the navigation buttons in the parent wrapper
      // Use closest to find the wrapper that contains this swiper and its buttons
      const wrapper = swiperContainer.closest('.swiper-container-wrapper');
      const nextButton = wrapper ? wrapper.querySelector('.product-grid-nav-next') : null;
      const prevButton = wrapper ? wrapper.querySelector('.product-grid-nav-prev') : null;
      
      // If not found via wrapper, try using unique IDs as fallback
      if (!nextButton || !prevButton) {
        const containerId = swiperContainer.id;
        if (containerId) {
          const sectionIdMatch = containerId.match(/product-grid-swiper-(.+)/);
          const sectionId = sectionIdMatch ? sectionIdMatch[1] : null;
          if (sectionId) {
            const nextById = document.getElementById(`product-grid-next-${sectionId}`);
            const prevById = document.getElementById(`product-grid-prev-${sectionId}`);
            if (nextById && !nextButton) nextButton = nextById;
            if (prevById && !prevButton) prevButton = prevById;
          }
        }
      }
      
      // Set nav button heights based on first product card image
      this.updateNavButtonHeights(swiperContainer, prevButton, nextButton);
      
      // Loop requires total slides >= slidesPerView * 2 (otherwise Swiper stops after one slide)
      const minSlidesForLoop = Math.max(columnCount * 2, 4);
      const canLoop = !isHomepageGrid && infinite && slideCount >= minSlidesForLoop;

    // Force buttons to be visible
    if (nextButton) {
      nextButton.style.display = 'flex';
      nextButton.style.visibility = 'visible';
      nextButton.style.opacity = '1';
      nextButton.classList.remove('button-lock');
    }
    
    if (prevButton) {
      prevButton.style.display = 'flex';
      prevButton.style.visibility = 'visible';
      prevButton.style.opacity = '1';
      prevButton.classList.remove('button-lock');
    }
    
    // Determine initial column count for config
    const viewportWidth = window.innerWidth;
    let initialColumnCount = mobileColumnCount;
    if (viewportWidth >= 1024) {
      initialColumnCount = columnCount;
    } else if (viewportWidth >= 768) {
      initialColumnCount = 2;
    }
    if (isHomepageGrid) {
      if (viewportWidth >= 1440) {
        initialColumnCount = 4.5;
      } else if (viewportWidth >= 1024) {
        initialColumnCount = 4.25;
      } else if (viewportWidth >= 768) {
        initialColumnCount = 3.25;
      } else {
        initialColumnCount = 1.75;
      }
    }

    // CRITICAL: Ensure SwiperJS is loaded before creating config
    const globalWindow = /** @type {Record<string, any>} */ (window);
    const SwiperConstructor = globalWindow.Swiper;
    if (typeof SwiperConstructor !== 'function') {
      console.warn('SwiperJS not loaded yet, retrying initialization...');
      // Retry after a short delay
      setTimeout(() => this._doInitialize(swiperContainer), 100);
      return;
    }

    // Product Grid specific swiper configuration with enhanced mobile settings
    // OPTIMIZED: Use correct initial column count immediately (no delay)
    // All params are set before initialization to ensure proper LCP
    const swiperConfig = {
      loop: canLoop,
      speed: 400, // Explicit slide transition duration (prevents no-animation when update() runs)
      slidesPerView: initialColumnCount, // Use calculated initial column count immediately
      spaceBetween: isHomepageGrid ? 4 : 16,
      slidesPerGroup: 1, // Move one slide at a time
      autoHeight: true, // CRITICAL: Auto-adjust height to content, prevents full-screen height bug
      navigation: showArrows && nextButton && prevButton ? {
        nextEl: nextButton,
        prevEl: prevButton,
      } : false,
      autoplay: autoplay ? {
        delay: parseInt(autoplaySpeed),
        disableOnInteraction: false,
        pauseOnMouseEnter: true, // Pause on hover for product grids
      } : false,
      allowTouchMove: true,
      grabCursor: true,
      // Product grid specific settings
      nested: true,
      watchSlidesProgress: false,
      watchSlidesVisibility: false,
      // Enhanced touch support for product grids
      touchRatio: 1,
      touchAngle: 45,
      simulateTouch: true,
      // Better performance for product grids
      preloadImages: false,
      lazy: {
        loadPrevNext: true,
        loadPrevNextAmount: 2,
      },
      // Product grid specific mobile behavior
      centeredSlides: false,
      freeMode: false,
      watchOverflow: true,
      // Update swiper params after initialization
      breakpoints: isHomepageGrid ? {
        320: {
          slidesPerView: 1.75,
          spaceBetween: 4,
          slidesPerGroup: 1,
        },
        480: {
          slidesPerView: 1.75,
          spaceBetween: 4,
          slidesPerGroup: 1,
        },
        768: {
          slidesPerView: 3.25,
          spaceBetween: 4,
          slidesPerGroup: 3,
        },
        1024: {
          slidesPerView: 4.25,
          spaceBetween: 4,
          slidesPerGroup: 4,
        },
        1440: {
          slidesPerView: 4.5,
          spaceBetween: 4,
          slidesPerGroup: 4,
        }
      } : {
        // Mobile-first approach with specific product grid settings
        320: {
          slidesPerView: 1.75,
          spaceBetween: 16,
          slidesPerGroup: 1,
        },
        480: {
          slidesPerView: 1.75,
          spaceBetween: 16,
          slidesPerGroup: 1,
        },
        768: {
          slidesPerView: 3,
          spaceBetween: 16,
          slidesPerGroup: 1,
        },
        1200: {
          slidesPerView: 3,
          spaceBetween: 16,
          slidesPerGroup: 1,
        },
        1440: {
          slidesPerView: columnCount,
          spaceBetween: 16,
          slidesPerGroup: 1,
        }
      }
    };

    // Initialize product grid swiper
    try {
      // OPTIMIZED: Initialize immediately without waiting for visibility check
      // This ensures columns are set correctly on page load for LCP
      // Verify swiperContainer is still valid before proceeding
      if (!swiperContainer || !(swiperContainer instanceof Element) || !swiperContainer.isConnected) {
        return;
      }
      
      // FORCE reset all heights BEFORE initializing swiper
      swiperContainer.style.height = 'auto';
      swiperContainer.style.minHeight = '0';
      swiperContainer.style.maxHeight = 'none';
      
      const swiperWrapper = swiperContainer.querySelector('.swiper-wrapper');
      if (swiperWrapper) {
        swiperWrapper.style.height = 'auto';
        swiperWrapper.style.minHeight = '0';
        swiperWrapper.style.maxHeight = 'none';
      }
      
      const slides = swiperContainer.querySelectorAll('.swiper-slide');
      slides.forEach(slide => {
        slide.style.height = 'auto';
        slide.style.minHeight = '0';
        slide.style.maxHeight = 'none';
      });
      
      // SwiperConstructor already checked above, use it here
      // Initialize immediately with correct column count and all params set
      const swiper = new SwiperConstructor(swiperContainer, swiperConfig);
      this.swiperInstances.set(containerId, swiper);

      // Homepage uses one contextual arrow: forward on the right, then back on
      // the left once the final group of products is reached.
      if (isHomepageGrid && wrapper) {
        const syncHomepageArrow = () => {
          if (!swiper || swiper.destroyed) return;
          const isStatic = !showArrows || swiper.isLocked || swiper.snapGrid.length <= 1;
          const isAtEnd = !isStatic && swiper.isEnd && !swiper.isBeginning;
          wrapper.classList.toggle('homepage-carousel-static', isStatic);
          wrapper.classList.toggle('homepage-carousel-at-end', isAtEnd);
        };

        swiper.on('slideChange', syncHomepageArrow);
        swiper.on('reachEnd', syncHomepageArrow);
        swiper.on('fromEdge', syncHomepageArrow);
        swiper.on('breakpoint', syncHomepageArrow);
        swiper.on('resize', syncHomepageArrow);

        if (prevButton) {
          if (prevButton._suniceHomepageResetHandler) {
            prevButton.removeEventListener('click', prevButton._suniceHomepageResetHandler, true);
          }
          const resetHomepageCarousel = (event) => {
            if (!wrapper.classList.contains('homepage-carousel-at-end')) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            swiper.slideTo(0);
          };
          prevButton._suniceHomepageResetHandler = resetHomepageCarousel;
          prevButton.addEventListener('click', resetHomepageCarousel, true);
        }

        requestAnimationFrame(syncHomepageArrow);
      }

      // Mark as initialized immediately so lazy-loaded sections can reveal on next frame
      swiperContainer.classList.add('swiper-initialized');
      requestAnimationFrame(() => {
        // Freeze height to prevent CLS when min-height: auto applies (sunice-swiper-initial-columns.css)
        swiperContainer.style.minHeight = swiperContainer.offsetHeight + 'px';
        void swiperContainer.offsetWidth;
      });

      // Force update immediately and after images load
      const fixHeights = () => {
        // Guard: swiper must exist, not be destroyed, and have valid params (avoid "slidesPerView" of undefined)
        if (!swiper || !swiper.update || swiper.destroyed || !swiper.params) {
          return;
        }
        try {
          // Force all heights to auto
          swiperContainer.style.height = 'auto';
          swiperContainer.style.minHeight = '0';
          swiperContainer.style.maxHeight = 'none';
          
          if (swiperWrapper) {
            swiperWrapper.style.height = 'auto';
            swiperWrapper.style.minHeight = '0';
            swiperWrapper.style.maxHeight = 'none';
          }
          
          slides.forEach(slide => {
            slide.style.height = 'auto';
            slide.style.minHeight = '0';
            slide.style.maxHeight = 'none';
          });
          
          // Update autoHeight only – do NOT call update/updateSlides/updateSlidesClasses here,
          // as they can interrupt an in-progress slide transition and kill the animation
          if (swiper.params && swiper.updateAutoHeight) {
            swiper.updateAutoHeight();
          }
        } catch (e) {
          // Swiper may be destroyed by the time timeouts/image load fire; ignore
        }
      };
      
      // Fix heights immediately
      setTimeout(fixHeights, 50);
      
      // Wait for images and fix again
      const allImages = swiperContainer.querySelectorAll('img');
      let imagesLoaded = 0;
      const totalImages = allImages.length;
      
      if (totalImages === 0) {
        setTimeout(fixHeights, 200);
      } else {
        allImages.forEach(img => {
          if (img.complete) {
            imagesLoaded++;
          } else {
            img.addEventListener('load', fixHeights, { once: true });
            img.addEventListener('error', fixHeights, { once: true });
          }
        });
        
        if (imagesLoaded === totalImages) {
          setTimeout(fixHeights, 200);
        }
        
        setTimeout(fixHeights, 300);
      }
      
      // Defer swatch carousel initialization to reduce initial load time
      if (swiperWrapper) {
        const initColorSwipers = () => {
          if (window.SuniceSwatchCarousel && typeof window.SuniceSwatchCarousel.init === 'function') {
            window.SuniceSwatchCarousel.init(swiperWrapper);
          }
        };
        
        // Defer swatch carousel initialization
        if (window.requestIdleCallback) {
          requestIdleCallback(initColorSwipers, { timeout: 2000 });
        } else {
          setTimeout(initColorSwipers, 500);
        }
      }
      
      // Return null since swiper is initialized asynchronously
      // The swiper instance is stored in this.swiperInstances
      return null;
    } catch (error) {
      return null;
    }
    };
    
    // Check if slides are already available
    const initialSlideCount = swiperContainer.querySelectorAll('.swiper-slide').length;
    
    if (initialSlideCount > 0) {
      // Slides are already available, initialize immediately
      initializeSwiper();
    } else {
      // Wait for slides to be available (they might be loading asynchronously)
      const waitForSlides = (retries = 50) => {
        const slideCount = swiperContainer.querySelectorAll('.swiper-slide').length;
        
        if (slideCount === 0 && retries > 0) {
          setTimeout(() => waitForSlides(retries - 1), 100);
          return;
        }
        
        if (slideCount === 0) {
          return;
        }
        
        // Continue with initialization now that slides are available
        initializeSwiper();
      };
      
      // Start waiting for slides
      waitForSlides();
    }
  }

  destroyProductGridSwipers() {
    this.swiperInstances.forEach((swiper) => {
      if (swiper?.el) {
        this._detachProductGridResizeListener(swiper.el);
      }
      if (swiper && swiper.destroy) {
        swiper.destroy(true, true);
      }
    });
    this.swiperInstances.clear();
  }
  
  /**
   * Update nav button heights based on first product card image
   * @param {HTMLElement} swiperContainer - The swiper container
   * @param {HTMLElement} prevButton - Previous button element
   * @param {HTMLElement} nextButton - Next button element
   */
  updateNavButtonHeights(swiperContainer, prevButton, nextButton) {
    if (!swiperContainer) return;
    
    const updateHeights = () => {
      // Find the first product card image in the swiper
      const firstSlide = swiperContainer.querySelector('.swiper-slide:first-child, .product-grid-swiper-slide:first-child');
      if (!firstSlide) return;
      
      const firstImageContainer = firstSlide.querySelector('.product-card__image');
      if (!firstImageContainer) return;
      
      // Get the height of the image container
      const imageHeight = firstImageContainer.offsetHeight;

      const homepageSection = swiperContainer.closest('.sunice-product-grid-section--homepage');
      if (homepageSection && imageHeight > 0) {
        const wrapper = swiperContainer.closest('.swiper-container-wrapper');
        if (wrapper) {
          wrapper.style.setProperty('--homepage-product-image-height', `${imageHeight}px`);
          wrapper.style.setProperty('--homepage-product-arrow-top', `${imageHeight / 2}px`);
        }
        if (prevButton) prevButton.style.removeProperty('height');
        if (nextButton) nextButton.style.removeProperty('height');
        return;
      }
      
      // Set both button heights
      if (prevButton && imageHeight > 0) {
        prevButton.style.height = `${imageHeight}px`;
      }
      if (nextButton && imageHeight > 0) {
        nextButton.style.height = `${imageHeight}px`;
      }
    };
    
    // Update immediately
    updateHeights();
    
    // Wait for images to load, then update again
    const images = swiperContainer.querySelectorAll('.product-card__image img');
    if (images.length > 0) {
      let loadedCount = 0;
      const totalImages = images.length;
      
      images.forEach(img => {
        if (img.complete) {
          loadedCount++;
          if (loadedCount === totalImages) {
            setTimeout(updateHeights, 50);
          }
        } else {
          img.addEventListener('load', () => {
            loadedCount++;
            if (loadedCount === totalImages) {
              setTimeout(updateHeights, 50);
            }
          }, { once: true });
        }
      });
      
      // Fallback: update after a short delay even if images don't load
      setTimeout(updateHeights, 500);
    }
    
    // Update on window resize with debounce
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateHeights, 100);
    };
    
    // Store resize handler to avoid duplicates - use a unique key per container
    const resizeKey = `resize-${swiperContainer.id || Math.random()}`;
    if (!swiperContainer.dataset.resizeHandlerAttached) {
      this._detachProductGridResizeListener(swiperContainer);
      window.addEventListener('resize', handleResize);
      swiperContainer._suniceProductGridResizeHandler = handleResize;
      swiperContainer.dataset.resizeHandlerAttached = resizeKey;
    }
    
    // Also update when swiper updates (e.g., after slide changes)
    const swiperInstance = this.swiperInstances.get(swiperContainer);
    if (swiperInstance) {
      swiperInstance.on('update', updateHeights);
      swiperInstance.on('resize', updateHeights);
    }
  }
  };

  // Export for use in main swiper file
  window.SuniceProductGridSwiper = SuniceProductGridSwiper;
}
